import api from './base/BaseApi';
import {
  TGetTransactionDetailsData,
  TAttachment,
  TCreateOrUpdateReceiptExemptionRequestData,
  TCreateOrUpdateTransactionNoteRequestData,
  TGetBusinessOrAccountTransactionsRequestParams,
  TTransactionListPaginatedResponse,
  TExportTransactionResponse,
  TExportTransactionsRequestBody
} from './types/TransactionData';

import { handleApiError } from './base/handleApiError';
import { getBusinessOrAccountTransactionHistoryParams } from './helpers/getBusinessOrAccountTransactionHistoryParams';

export const getTransactionDetails = async (transactionId: string) =>
  api
    .get<TGetTransactionDetailsData>(
      `api/v1/transactions/${transactionId}/details-with-attachments`
    )
    .then((response) => response)
    .catch(handleApiError);

export const getTransactionAttachment = async (transactionId: string, attachmentId: string) =>
  api
    .get<TAttachment>(`api/v1/transactions/${transactionId}/attachments/${attachmentId}`)
    .then((response) => response)
    .catch(handleApiError);

export const addTransactionAttachment = async (transactionId: string, attachmentData: FormData) =>
  api
    .multiPartPost<TAttachment>(`api/v1/transactions/${transactionId}/attachments`, attachmentData)
    .then((response) => response)
    .catch(handleApiError);

export const deleteTransactionAttachment = async (transactionId: string, attachmentId: string) =>
  api
    .delete(`api/v1/transactions/${transactionId}/attachments/${attachmentId}`)
    .then((response) => response)
    .catch(handleApiError);

// Checks if the current attachment URL is working (browser should also cache these assets if it has already been loaded) otherwise fetch a fresh one
export const getWorkingTransactionAttachmentUrl = async (
  transactionId: string,
  attachment: TAttachment
): Promise<string> =>
  fetch(attachment.url)
    .then(async (response) => {
      if (!response.ok) {
        const res = await getTransactionAttachment(transactionId, attachment.id);

        return res.data.url;
      }

      return attachment.url;
    })
    .catch(async () => {
      const res = await getTransactionAttachment(transactionId, attachment.id);

      return res.data.url;
    });

export const updateTransactionReceiptExemptionReason = async (
  transactionId: string,
  body: TCreateOrUpdateReceiptExemptionRequestData
) =>
  api
    .patch<null, TCreateOrUpdateReceiptExemptionRequestData>(
      `api/v1/transactions/${transactionId}/details-with-attachments/receipt-exemption-reasons`,
      body
    )
    .then((response) => response)
    .catch(handleApiError);

export const updateTransactionNote = async (
  transactionId: string,
  body: TCreateOrUpdateTransactionNoteRequestData
) =>
  api
    .patch<null, TCreateOrUpdateTransactionNoteRequestData>(
      `api/v1/transactions/${transactionId}/details-with-attachments`,
      body
    )
    .then((response) => response)
    .catch(handleApiError);

export const getBusinessTransactions = async (
  businessId: string,
  options: TGetBusinessOrAccountTransactionsRequestParams
) =>
  api
    .get<TTransactionListPaginatedResponse>(
      `api/v1/transactions/businesses/${businessId}?${getBusinessOrAccountTransactionHistoryParams(options)}`
    )
    .then((response) => response)
    .catch(handleApiError);

export const getAccountTransactions = async (
  accountId: string,
  options: TGetBusinessOrAccountTransactionsRequestParams
) =>
  api
    .get<TTransactionListPaginatedResponse>(
      `api/v1/transactions/accounts/${accountId}?${getBusinessOrAccountTransactionHistoryParams(options)}`
    )
    .then((response) => response)
    .catch(handleApiError);

export const exportBusinessTransactions = async (
  businessId: string,
  body: TExportTransactionsRequestBody
) =>
  api
    .post<TExportTransactionResponse, TExportTransactionsRequestBody>(
      `api/v1/transactions/businesses/${businessId}/export`,
      body
    )
    .then((response) => response)
    .catch(handleApiError);

export const exportAccountTransaction = async (
  accountId: string,
  body: TExportTransactionsRequestBody
) =>
  api
    .post<TExportTransactionResponse, TExportTransactionsRequestBody>(
      `api/v1/transactions/accounts/${accountId}/export`,
      body
    )
    .then((response) => response)
    .catch(handleApiError);
